import React, { JSXElementConstructor, createElement, isValidElement } from 'react';

type ConditionalWrapperProps = {
  /** Condition to check */
  condition: boolean;

  /** Wraps the children. Can not be a string, number, int. */
  wrapper:
    | JSXElementConstructor<any>
    | JSX.Element
    | ((children: React.ReactNode) => React.ReactNode);

  /** Children */
  children: React.ReactNode | React.ReactNode[];
};

const getWrapper = ({
  wrapper,
  children,
}: Pick<ConditionalWrapperProps, 'wrapper' | 'children'>) => {
  const element = wrapper;

  const isElement = isValidElement(element);

  if (isElement) {
    return createElement(element.type, element.props, children);
  }

  if (typeof element === 'function') {
    const wrappedFunction = element as (children: React.ReactNode) => React.ReactNode;
    return wrappedFunction(children);
  }
};

/**
 * Renders a wrapper component conditionally based on a given condition.
 *
 * @param condition - The condition to determine whether to render the wrapper component.
 * @param wrapper - The wrapper component to render if the condition is true.
 * @param children - The children components to render.
 * @returns The wrapped children components or the original children components.
 */
const ConditionalWrapper = ({ condition, wrapper, children }: ConditionalWrapperProps) =>
  condition ? getWrapper({ children, wrapper }) : children;

export { ConditionalWrapper };
